import { Component, OnInit } from '@angular/core';
import {
    ApplyColumnStateParams,
    ColDef,
    ColGroupDef,
    GridApi,
    GridOptions,
    GridReadyEvent
} from 'ag-grid-community';
import {
    AgGridColumns,
    AgGridFilterParams,
    AgGridOptionsBuilder
} from '../../Compliance/AgGrid';
import { CapRuleListDataSource } from './agGridDataSource';
import { CapRuleRepository } from '../../Compliance/Repositories/capRule.repository';
import { DecimalPipe, PercentPipe } from '@angular/common';
import {
    IPercentIncreaseCellRendererParams,
    PercentIncreaseCellRenderer
} from './agGridPercentIncreaseCellRenderer.component';
import { WeissmanModalService } from '../../Compliance/WeissmanModalService';
import { CapRuleDetailsComponent, ICapRuleDetailsParams } from '../Cap-Rule-Details/capRuleDetails.component';
import { RestrictService, Roles } from '../../Common/Permissions/restrict.service';
import {
    CapRuleListGridActionCellRendererComponent,
    ICellRendererParamsForCapRoleListGridAction
} from './agGridActionCellRenderer.component';
import {
    AgGridYesNoFloatingFilterComponent
} from '../../Compliance/AgGrid/FloatingFilters/agGridYesNoFloatingFilter.component';

@Component({
    templateUrl: './capRuleList.component.html',
    selector: 'cap-rule-list',
    standalone: false
})
export class CapRuleListComponent implements OnInit {
    constructor(
        private readonly _capRuleRepository: CapRuleRepository,
        private readonly _percentPipe: PercentPipe,
        private readonly _modalService: WeissmanModalService,
        private readonly _decimalPipe: DecimalPipe,
        _restrictService: RestrictService) {
        this.canEditRoles = _restrictService.isInRoles([Roles.JURISDICTIONSETUPS])
    }

    gridId: System.Guid = 'A41ED72F-35AC-4287-BA1E-AE410842C057';
    gridApi: GridApi;
    taxYearBegin: number = 0;
    taxYearsPrior: number = 2;
    canEditRoles: boolean = false;

    gridOptions: GridOptions = new AgGridOptionsBuilder()
        .withColumnPinning()
        .buildDefault(this);

    private _gridDataSource: CapRuleListDataSource;

    get refreshing(): boolean {
        return this._gridDataSource && this._gridDataSource.isRefreshing;
    }

    async ngOnInit(): Promise<void> {
        this.taxYearBegin = new Date().getFullYear();
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;

        this._setColumns();
        this._setDataSource();
    }

    refresh(): void {
        this._refreshDataSource();
    }

    showTaxYearChanged() {
        this._setColumns();
        this.gridApi.sizeColumnsToFit();
    }

    public async addCapRule(): Promise<void> {
        const params: ICapRuleDetailsParams = {
            capRule: null
        };
        const result = await this._modalService.showAsync(CapRuleDetailsComponent, params, 'modal-lg');

        if (!result) {
            return Promise.resolve();
        }

        this._refreshDataSource();
    }

    private _refreshDataSource() {
        if (!this._gridDataSource) {
            const success = this._setDataSource();
            if (!success) {
                return;
            }
        }
        this._gridDataSource.refresh();
    }

    private _setColumns() {
        const mainColumns: ColDef[] = [
            {
                headerName: 'State',
                field: 'stateAbbr',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'Property Type',
                field: 'propertyType',
                width: AgGridColumns.textColumnLargeWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'Asmt Component',
                field: 'assessmentComponentName',
                width: AgGridColumns.textColumnLargeWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'Tax Year Begin',
                field: 'taxYearBegin',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParamsWithEqualTo,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                valueFormatter: (params) => {
                    if (!params) {
                        return '';
                    }

                    return !params.value
                        ? 'Infinite'
                        : params.value;
                }
            },
            {
                headerName: 'Tax Year End',
                field: 'taxYearEnd',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParamsWithEqualTo,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                valueFormatter: (params) => {
                    if (!params) {
                        return '';
                    }

                    return !params.value
                        ? 'Infinite'
                        : params.value;
                }
            },
            {
                headerName: 'Pre-Defined Rule',
                field: `capRulePredefinedName`,
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'Cap Value Range (Max)',
                field: 'capValueRange',
                width: AgGridColumns.textColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                valueFormatter: (params) => {
                    if (!params || !params.value) {
                        return '';
                    }

                    return `Calc cap amt < ${this._decimalPipe.transform(params.value, '1.0-0')}`;
                }
            },
            {
                headerName: 'Cap May Exceed AV',
                field: 'mayCapExceedAV',
                width: AgGridColumns.textColumnWidth,
                filter: 'agYesNoColumnFilter',
                filterParams: AgGridFilterParams.yesNoFilterParams,
                floatingFilterComponent: AgGridYesNoFloatingFilterComponent,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                valueFormatter: (params) => {
                    if (!params) {
                        return '';
                    }

                    return params.value
                        ? 'Yes'
                        : 'No';
                }
            },
            {
                headerName: 'Status Update',
                field: 'statusUpdate',
                width: AgGridColumns.textColumnLargeWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                hide: true
            }];

        const taxYears: number[] = new Array(this.taxYearsPrior + 1).fill(undefined).map((_, i) => this.taxYearBegin - i);
        const percentColumns: ColDef[] = taxYears
            .map(year => {
                return {
                    headerName: `${year}`,
                    field: `${year}`,
                    width: AgGridColumns.numericColumnWidth,
                    cellRenderer: PercentIncreaseCellRenderer,
                    cellRendererParams: {
                        value: this._getRateIncrease.bind(this),
                        year: year
                    } as IPercentIncreaseCellRendererParams,
                    sortable: false
                } as ColDef
            })
            .reduce((columns, column) => {
                    columns.push(column);
                    return columns;
                },
                [{
                    headerName: 'Default',
                    field: 'defaultAnnualPercentIncrease',
                    width: AgGridColumns.numericColumnWidth,
                    filter: 'agNumberColumnFilter',
                    filterParams: AgGridFilterParams.numberFilterParams,
                    floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                    valueFormatter: (params) => {
                        if (!params || !params.value) {
                            return '';
                        }

                        return this._percentPipe.transform(params.value, '1.6-6');
                    }
                }]);

        const actionsColDef: ColDef = {
            colId: 'grid-column-actions',
            headerName: '',
            field: 'actions',
            width: AgGridColumns.getActionColumnWidth(1),
            minWidth: AgGridColumns.getActionColumnWidth(1),
            maxWidth: AgGridColumns.getActionColumnWidth(1),
            suppressSizeToFit: true,
            suppressAutoSize: true,
            resizable: false,
            suppressColumnsToolPanel: true,
            lockPinned: true,
            pinned: 'right',
            lockVisible: true,
            sortable: false,
            cellRendererSelector: (params) => {
                if (!params.data?.totalsRow && !params.data?.selectedTotalsRow) {
                    return {
                        component: CapRuleListGridActionCellRendererComponent,
                        params: {
                            viewDetails: this._viewCapRuleDetails.bind(this)
                        } as ICellRendererParamsForCapRoleListGridAction
                    };
                }
            }
        };


        const defaultSortModel: ApplyColumnStateParams = {
            state: [{ colId: 'stateAbbr', sort: 'asc' }],
            defaultState: { sort: null }
        };

        const columns: ColDef[] = [
            { headerName: '', children: mainColumns} as ColGroupDef,
            { headerName: 'Annual Percent Increase', children: percentColumns } as ColGroupDef,
            { headerName: '', children: [actionsColDef]} as ColGroupDef];

        this.gridApi.setGridOption('columnDefs', columns);
        this.gridApi.applyColumnState(defaultSortModel);
        this.gridApi.sizeColumnsToFit();
    }

    private _setDataSource(): boolean {
        if (!this.gridApi || this._gridDataSource) {
            return;
        }

        this._gridDataSource = new CapRuleListDataSource(this.gridApi, this._capRuleRepository);
        this.gridApi.setGridOption('datasource', this._gridDataSource);
        return true;
    }

    private _getRateIncrease(params, year: number): number {
        if (!params.data) {
            return null;
        }

        const increaseInfo = params.data.annualPercentIncreases.find(x => x.year === year);

        return increaseInfo
            ? increaseInfo.value
            : null;
    }

    private async _viewCapRuleDetails(params: ICellRendererParamsForCapRoleListGridAction): Promise<void> {
        const capRule = params.data as Core.CapRuleModel;

        const modalParams: ICapRuleDetailsParams = {
            capRule: capRule
        };

        const result = await this._modalService.showAsync(CapRuleDetailsComponent, modalParams, 'modal-lg');

        if (!(result && (result.hasChanges || result.isDeleted))) {
            return Promise.resolve();
        }

        if (result.isDeleted) {
            this._refreshDataSource();
        } else {
            params.node.setData(result.capRule);
            this.gridApi.redrawRows({rowNodes: [params.node]});
            return Promise.resolve();
        }
    }
}
